<template>
  <div class="d-flex flex-column privilege-form">
    <span class="font-weight-bold font-size-16">Media</span>
    <LanguageSelect
      :value="selectedLanguage"
      :langs="languages"
      :contentFilled="filledLangs"
      :error="formErrorState"
      short-button-label
      :style="{ top: 0 }"
      @copyFromDefault="handleCopyFromDefault"
      @clear="handleClearLang"
      @input="handleLanguageChange"
    />

    <a-col style="display: flex; flex-direction: column; gap: 1rem">
      <a-form-model-item
        :colon="false"
        label="Image"
        :prop="`${selectedLanguage}.base.image.url`"
        :rules="[
          {
            required: true,
            message: 'Image is required',
            trigger: ['change'],
          },
        ]"
      >
        <ImageUploader
          v-model="form[selectedLanguage].base.image.url"
          :file-width="360"
          :file-height="360"
          :container-width="200"
          aspect-ratio="1 / 1"
          has-delete-overlay
          @change="handleMediaImageUpload"
        />
      </a-form-model-item>
      <div class="normal-size">
        <a-form-model-item
          label="Completion date"
          :colon="false"
          class="d-inline-block"
        >
          <a-date-picker
            v-model="form[selectedLanguage].base.completionDate"
            :show-time="form[selectedLanguage].base.showTime"
            :format="
              form[selectedLanguage].base.showTime
                ? 'YYYY-MM-DD HH:mm:ss'
                : 'YYYY-MM-DD'
            "
            type="date"
            size="large"
            placeholder="Select date"
            :disabled="localLoading"
            :disabled-date="disableDatesBeforeToday"
            style="width: 250px"
            inputReadOnly
          />
        </a-form-model-item>
      </div>

      <a-form-model-item
        :colon="false"
        class="d-flex align-items-center switch"
      >
        <template #label>
          Completion time
          <a-tooltip
            slot="suffix"
            :title="`Show completion time on the card`"
          >
            <a-icon type="info-circle" class="text-primary" />
          </a-tooltip>
        </template>
        <a-switch v-model="form[selectedLanguage].base.showTime" />
      </a-form-model-item>

      <a-form-model-item
        label="Title"
        :prop="`${selectedLanguage}.base.title`"
        :rules="[
          {
            required: true,
            message: 'Title is required',
            trigger: ['blur'],
          },
        ]"
        :colon="false"
        class="normal-size"
      >
        <a-input
          v-model="form[selectedLanguage].base.title"
          placeholder="Name"
          size="large"
          :maxLength="50"
          :disabled="localLoading"
        />
        <div class="text-count">
          <span>{{ mediaBase.title?.length }} / 50</span>
        </div>
      </a-form-model-item>

      <a-form-model-item
        :colon="false"
        label="Redirect"
        class="d-flex align-items-center switch"
      >
        <a-switch v-model="form[selectedLanguage].base.hasButton" />
      </a-form-model-item>

      <template v-if="mediaBase.hasButton">
        <a-form-model-item
          label="Button name"
          :prop="`${selectedLanguage}.buttonProps.buttonName`"
          class="normal-size"
          :colon="false"
          :rules="[
            {
              required: true,
              message: 'Button name is required',
              trigger: ['blur', 'change'],
            },
          ]"
          required
        >
          <a-input
            v-model="form[selectedLanguage].buttonProps.buttonName"
            size="large"
            :disabled="localLoading"
          />
        </a-form-model-item>

        <a-radio-group
          name="radioGroup"
          v-model="form[selectedLanguage].buttonProps.buttonType"
          class="mt-2"
        >
          <a-radio value="link"> Link </a-radio>
          <a-radio value="dialogue">
            Bottom dialogue
            <a-tooltip
              title="This section opens in the application through the lower dialog when you click on the privilege"
            >
              <a-icon type="info-circle" class="text-primary" />
            </a-tooltip>
          </a-radio>
        </a-radio-group>

        <template v-if="form[selectedLanguage].buttonProps.buttonType === 'link'">
          <PrivilegeMediaLink
            ref="link-1"
            v-model="form[selectedLanguage].buttonProps.link"
            :loading="localLoading"
            :sports="sports"
            :linkTemplates="linkTemplates"
            @openModal="
              $emit(
                'openLinkModal',
                form[selectedLanguage].buttonProps.link
              )
            "
          />
        </template>

        <template v-else-if="form[selectedLanguage].buttonProps.buttonType === 'dialogue'">
          <a-form-model-item label="Image" :colon="false">
            <ImageUploader
              v-model="selectedImage.url"
              :file-width="360"
              :file-height="360"
              aspect-ratio="1 / 1"
              variant="small"
              show-edit-button
              has-delete-overlay
              @change="handleDialogImageUpload"
            />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            :prop="`${selectedLanguage}.buttonProps.dialogue.title`"
            :rules="[
              {
                required: true,
                message: 'Title is required',
                trigger: ['change', 'blur'],
              },
            ]"
            class="bigger-size"
            required
          >
            <template #label>
              <span> Title </span>
              <a-tooltip
                slot="suffix"
                title="This value should be inherited from the card preview"
              >
                <a-icon type="info-circle" class="text-primary" />
              </a-tooltip>
            </template>
            <quill-editor
              v-model="form[selectedLanguage].buttonProps.dialogue.title"
              :options="quillEditorOptions"
              @change="($event) => validateQuillLength($event, 50)"
            />
            <div class="text-count">
              <span>
                {{
                  stripHtml(
                    form[selectedLanguage].buttonProps.dialogue.title
                  ).length
                }}
                / 50
              </span>
            </div>
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label="Text"
            :prop="`${selectedLanguage}.buttonProps.dialogue.text`"
            :rules="[
              {
                required: true,
                message: 'Text is required',
                trigger: ['change', 'blur'],
              },
            ]"
            class="bigger-size quill-text"
            required
          >
            <quill-editor
              v-model="form[selectedLanguage].buttonProps.dialogue.text"
              :options="quillEditorOptions"
              @change="($event) => validateQuillLength($event, 10000)"
            />
            <div class="text-count">
              <span>
                {{
                  stripHtml(
                    form[selectedLanguage].buttonProps.dialogue.text
                  ).length?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                }}
                / 10 000
              </span>
            </div>
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label="Button"
            class="d-flex align-items-center switch"
          >
            <a-switch
              v-model="
                form[selectedLanguage].buttonProps.dialogue.hasButton
              "
            />
          </a-form-model-item>

          <template v-if="dialogueProps.hasButton">
            <a-form-model-item
              label="Button name"
              :prop="`${selectedLanguage}.buttonProps.dialogue.buttonName`"
              class="normal-size"
              :colon="false"
              :rules="[
                {
                  required: true,
                  message: 'Button name is required',
                  trigger: ['blur', 'change'],
                },
              ]"
              required
            >
              <a-input
                v-model="
                  form[selectedLanguage].buttonProps.dialogue.buttonName
                "
                size="large"
                :disabled="localLoading"
              />
            </a-form-model-item>
            <PrivilegeMediaLink
              ref="link-2"
              v-model="form[selectedLanguage].buttonProps.dialogue.link"
              :loading="localLoading"
              :sports="sports"
              :linkTemplates="linkTemplates"
              @openModal="
                $emit(
                  'openLinkModal',
                  form[selectedLanguage].buttonProps.dialogue.link
                )
              "
            />
          </template>
        </template>
      </template>
    </a-col>
  </div>
</template>
<script>
import PrivilegeMediaLink from '@/views/apps/privileges/form/link'
import PrivilegeService from '@/services/api/apiPrivilegeService'
import ImageUploader from '@/components/custom/imageUploader.vue'
import LanguageSelect from '@/components/custom/languageSelect.vue'

import { MEDIA_FORM_FIELDS } from '@/constants/privileges'

import { quillEditor } from 'vue-quill-editor'
import isEqual from 'lodash/isEqual'

import { DEFAULT_LANG } from '@/constants/lang'

const defaultImage = {
  id: null,
  url: '',
  file: null,
  loading: false,
}

export default {
  name: 'privilegeFormMedia',
  components: {
    PrivilegeMediaLink,
    quillEditor,
    ImageUploader,
    LanguageSelect,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    loading: Boolean,
    languages: Array,
    sports: Array,
    linkTemplates: Array,
    selectedLanguage: String,
    formRef: Object,
  },
  data() {
    return {
      imageDeleteConfirmVisible: false,
      localLoading: this.loading,
      form: this.value,
      defaultLang: DEFAULT_LANG,
      validationResult: true,
      quillEditorOptions: {
        placeholder: 'Edit here...',
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline'],
              ['blockquote'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ direction: 'rtl' }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [{ color: [] }, { background: [] }],
              [{ align: [] }],
              ['link'],
            ],
          },
        },
      },
    }
  },
  computed: {
    selectedContent() {
      return this.form[this.selectedLanguage]
    },
    mediaBase() {
      return this.selectedContent.base
    },
    linkProps: {
      get() {
        return this.form[this.selectedLanguage].buttonProps.link
      },
      set(value) {
        this.form[this.selectedLanguage].buttonProps.link = value
      },
    },
    dialogueProps() {
      return this.form[this.selectedLanguage].buttonProps.dialogue
    },
    selectedImage() {
      return this.selectedContent?.buttonProps?.dialogue?.image
    },
    isInitialForm() {
      return isEqual(this.selectedContent, MEDIA_FORM_FIELDS)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.validationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      const obj = this.languages.reduce((obj, { name }) => {
        obj[name] = !isEqual(this.form[name], MEDIA_FORM_FIELDS)
        return obj
      }, {})

      return obj
    },
  },
  methods: {
    stripHtml(htmlString) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlString

      return tempDiv.textContent || tempDiv.innerText || ''
    },
    disableDatesBeforeToday(currentDate) {
      return currentDate.valueOf() < this.$moment().add(-1, 'day').endOf('day')
    },
    async handleMediaImageUpload({ file, url }) {
      const baseObj = this.form[this.selectedLanguage].base

      try {
        const response = await PrivilegeService.uploadPrivilegeImage(file)

        const previewId = response?.data?.data?.id

        if (!previewId) {
          throw new Error()
        }

        baseObj.image.id = previewId
        baseObj.image.file = file
        baseObj.image.url = url
      } catch (e) {
        const errorMessage =
          e.response?.data?.error?.message ||
          'Something went wrong while uploading image'
        baseObj.image = {
          ...defaultImage,
        }
        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    async handleDialogImageUpload({ file, url }) {
      const dialogueObj =
        this.form[this.selectedLanguage].buttonProps.dialogue

      try {
        const response = await PrivilegeService.uploadPrivilegeImage(file)

        const previewId = response?.data?.data?.id

        if (!previewId) {
          throw new Error()
        }

        dialogueObj.image.id = previewId
        dialogueObj.image.file = file
        dialogueObj.image.url = url
      } catch (e) {
        const errorMessage =
          e.response?.data?.error?.message ||
          'Something went wrong while uploading image'
        dialogueObj.image = {
          ...defaultImage,
        }
        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    handleLanguageChange(lang) {
      this.$emit('selectLanguage', lang)
    },
    handleCopyFromDefault() {
      if (!this.form[DEFAULT_LANG] || !this.form[this.selectedLanguage]) {
        return
      }

      const defaultForm = this.form[DEFAULT_LANG]

      this.form[this.selectedLanguage] = JSON.parse(
        JSON.stringify(defaultForm),
      )
    },
    validateQuillLength(e, maxLength) {
      const text = e.text.trim() ?? ''

      if (maxLength && maxLength < text.length) {
        e.quill.setText(text.substring(0, maxLength))
      }
    },
    validateForm() {
      if (!this.formRef) return

      this.formRef.validate((valid) => {
        this.validationResult = valid
      })
    },
    handleClearLang(lang) {
      this.form[lang] = Object.assign({}, MEDIA_FORM_FIELDS)

      this.formRef.clearValidate()
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.form = newVal
      },
    },
    selectedContent: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateForm())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }

      this.validateForm()
    },
  },
}
</script>
<style lang="scss">
.quill-text {
  .ant-form-item-children {
    display: inline-block;
    width: 100%;
  }

  .ql-container {
    height: 100px;
  }
}

.language-select {
  position: absolute !important;
  right: 0;

  .ant-select-selection-selected-value .subtitle {
    font-size: 8px !important;
    margin-left: 8px;
  }
}
</style>
